import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, canWrite } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FieldLabel } from "@/components/FieldLabel";
import ComboBox from "@/components/ComboBox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { plural } from "@/lib/utils";
import { formatDateTime } from "@/lib/utils";

/**
 * Whether the training worked — the question ISO 9001 §7.2, 14001 §7.2 and 45001 §7.2
 * each ask, and the one Abhyas could not answer at all until now.
 *
 * Deliberately a sibling of Attendance rather than part of it. Attendance is recorded on
 * the day; this is recorded weeks later, by whoever observed the person back on the job.
 * They are two different claims and the screen keeps them apart.
 *
 * Only people who were actually there appear as evaluable, and the counts under the
 * heading name their own population — "2 of 3 who attended", never "2 of 8 nominated" —
 * because a ratio whose halves count different sets is the mistake this project has
 * already shipped once.
 */
// The results that mean somebody looked — the server's EVALUATED.
const JUDGED = ["Effective", "Partially Effective", "Not Effective"];

const RESULT_TONE = {
  "Effective": "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  "Partially Effective": "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  "Not Effective": "bg-destructive/15 text-destructive-text",
  "Not Evaluated": "bg-muted text-muted-foreground",
};

// The methods that repeat, offered without being imposed — a written test, an
// observation, a supervisor sign-off are all valid answers under the standard and the
// organisation chooses. Anything else is still typeable.
const METHOD_HINTS = [
  "Written test",
  "Supervisor observation after 30 days",
  "On-the-job verification",
  "Self-assessment questionnaire",
  "Practical demonstration",
];

export default function EffectivenessTab({ trainingId, onSaved }) {
  const { user } = useAuth();
  const writable = canWrite(user);
  const [data, setData] = useState(null);
  const [method, setMethod] = useState("");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/trainings/${trainingId}/effectiveness`);
      setData(data);
      setMethod(data.evaluation_method || "");
      setDraft({});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not load evaluations"));
    }
  }, [trainingId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-muted-foreground py-8">Loading…</div>;

  const attended = (p) => p.attendance_status === "Present" || p.attendance_status === "Late";
  const eligible = data.participants.filter(attended);
  const valueFor = (p, field) =>
    draft[p.employee_id]?.[field] ?? (field === "result"
      ? (p.effectiveness_result || "Not Evaluated")
      : field === "score"
        ? (p.effectiveness_score ?? "")
        : (p.effectiveness_note || ""));

  const set = (employeeId, field, value) =>
    setDraft((d) => ({ ...d, [employeeId]: { ...d[employeeId], [field]: value } }));

  const dirty = Object.keys(draft).length > 0 || method !== (data.evaluation_method || "");
  // Judgements still standing on a training that is not delivered — after a Reopen, the
  // documented way to correct a register. They used to be hidden here while the counter
  // above read "Evaluated 1 of 1" and the attendance screen asked for exactly these to be
  // set back to Not Evaluated first (register #9).
  const standing = data.can_evaluate ? [] : data.participants.filter((p) => JUDGED.includes(p.effectiveness_result));

  const withdraw = async (p) => {
    setSaving(true);
    try {
      await api.post(`/trainings/${trainingId}/effectiveness`, {
        marks: [{ employee_id: p.employee_id, result: "Not Evaluated" }],
      });
      toast.success(`Withdrawn for ${p.full_name}`);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not withdraw"));
    }
    setSaving(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only what was touched. Posting the whole list would be harmless here — the
      // server refuses to stamp an evaluator on "Not Evaluated" — but sending rows
      // nobody edited is how the attendance screen once recorded ninety-nine people as
      // having been attended to.
      const marks = Object.entries(draft).map(([employee_id, v]) => {
        const mark = {
          employee_id,
          result: v.result ?? (data.participants.find((p) => p.employee_id === employee_id)
            ?.effectiveness_result || "Not Evaluated"),
        };
        // Only what was touched, field by field. A row lands in `draft` because ANY of
        // its three controls was used, and sending all three meant clicking a result
        // posted score: null and note: "" over values nobody had edited — silently
        // erasing the number and the sentence that justified the original judgement.
        if (v.score !== undefined) mark.score = v.score === "" ? null : Number(v.score);
        if (v.note !== undefined) mark.note = v.note;
        return mark;
      });
      await api.post(`/trainings/${trainingId}/effectiveness`, {
        evaluation_method: method, marks,
      });
      toast.success(marks.length ? `Recorded for ${plural(marks.length, "person", "people")}`
                                 : "Evaluation method saved");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not save"));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="effectiveness-tab">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="font-heading font-semibold">Was it effective?</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            ISO 9001, 14001 and 45001 each ask whether training worked, not only whether
            it happened. Recorded per person, and usually a while afterwards — this does
            not hold up certificates.
          </p>
        </div>
        {/* Each figure names the set it counts. */}
        <div className="flex gap-4 text-sm shrink-0">
          <div><div className="overline text-muted-foreground">Attended</div>
            <div className="font-medium mt-0.5" data-testid="eff-eligible">{data.eligible}</div></div>
          <div><div className="overline text-muted-foreground">Evaluated</div>
            <div className="font-medium mt-0.5" data-testid="eff-evaluated">{data.evaluated} of {data.eligible}</div></div>
          <div><div className="overline text-muted-foreground">Effective</div>
            <div className="font-medium mt-0.5" data-testid="eff-effective">
              {data.evaluated ? `${data.effective} of ${data.evaluated}` : "—"}
            </div></div>
        </div>
      </div>

      {!data.can_evaluate && (
        <div className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm"
             data-testid="eff-not-delivered">
          This training is <b>{data.workflow_status}</b>. Whether it worked can only be
          judged once it has been delivered — complete it first.
          {standing.length > 0 && " The judgements below were made before and still stand; withdraw one before marking that person as not having attended."}
        </div>
      )}

      {/* relative: the sr-only heading is position:absolute and would otherwise escape the
          scroll box and push the whole page sideways on a phone (register S2). */}
      {standing.length > 0 && (
        <div className="relative border border-border rounded-md overflow-x-auto" data-testid="eff-standing">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">Employee</th>
                {/* On a phone Attendance and Evaluated fold away, so Withdraw stays in view
                    instead of past the right edge of the scroll box. */}
                <th className="px-3 py-2 font-medium hidden sm:table-cell">Attendance</th>
                <th className="px-3 py-2 font-medium">Result</th>
                <th className="px-3 py-2 font-medium hidden sm:table-cell">Evaluated</th>
                {writable && <th className="px-3 py-2"><span className="sr-only">Withdraw</span></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {standing.map((p) => (
                <tr key={p.id} data-testid={`eff-standing-${p.employee_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{p.full_name}</div>
                    <div className="text-xs text-muted-foreground">{p.employee_code}</div>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground hidden sm:table-cell">{p.attendance_status}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${RESULT_TONE[p.effectiveness_result]}`}>
                      {p.effectiveness_result}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap hidden sm:table-cell">
                    {p.evaluated_at ? <><div>{p.evaluated_by_name || "—"}</div><div>{formatDateTime(p.evaluated_at)}</div></> : "—"}
                  </td>
                  {writable && (
                    <td className="px-3 py-2 text-right">
                      <Button variant="outline" size="sm" disabled={saving} onClick={() => withdraw(p)}
                              data-testid={`eff-withdraw-${p.employee_id}`}>Withdraw</Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.can_evaluate && (
        <>
          <div className="max-w-xl">
            <FieldLabel htmlFor="eff-method">How was it evaluated?</FieldLabel>
            <ComboBox id="eff-method" value={method} onChange={setMethod}
                      options={METHOD_HINTS} placeholder="A test, an observation, a sign-off…"
                      disabled={!writable} testid="eff-method" />
            <p className="text-xs text-muted-foreground mt-1.5">
              The standard does not name a method — it asks that you have one and that you
              recorded what it found. This prints on the participants export.
            </p>
          </div>

          {eligible.length === 0 ? (
            <div className="rounded-md border border-border px-4 py-6 text-sm text-muted-foreground text-center"
                 data-testid="eff-nobody">
              Nobody on this training is marked Present or Late, so there is nobody to
              evaluate yet. Mark attendance first.
            </div>
          ) : (
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">Employee</th>
                    <th className="px-3 py-2 font-medium">Attendance</th>
                    <th className="px-3 py-2 font-medium">Result</th>
                    <th className="px-3 py-2 font-medium w-24">Score</th>
                    <th className="px-3 py-2 font-medium">Note</th>
                    <th className="px-3 py-2 font-medium">Evaluated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {eligible.map((p) => (
                    <tr key={p.id} data-testid={`eff-row-${p.employee_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{p.full_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {p.employee_code}{p.designation ? ` · ${p.designation}` : ""}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{p.attendance_status}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {data.results.map((r) => {
                            const on = valueFor(p, "result") === r;
                            return (
                              <button key={r} type="button" disabled={!writable}
                                      onClick={() => set(p.employee_id, "result", r)}
                                      data-testid={`eff-${p.employee_id}-${r.replace(/\s+/g, "-")}`}
                                      className={`px-2 py-1 rounded text-xs border transition-colors ${
                                        on ? RESULT_TONE[r] + " border-transparent font-medium"
                                           : "border-border text-muted-foreground hover:bg-muted"}`}>
                                {r}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Input type="number" min="0" max="100" disabled={!writable}
                               value={valueFor(p, "score")}
                               onChange={(e) => set(p.employee_id, "score", e.target.value)}
                               aria-label={`Score for ${p.full_name}`}
                               data-testid={`eff-score-${p.employee_id}`} className="h-8" />
                      </td>
                      <td className="px-3 py-2">
                        <Input value={valueFor(p, "note")} disabled={!writable}
                               onChange={(e) => set(p.employee_id, "note", e.target.value)}
                               aria-label={`Note for ${p.full_name}`}
                               placeholder="What was found, or what was done about it"
                               data-testid={`eff-note-${p.employee_id}`} className="h-8" />
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                        {p.evaluated_at ? (
                          <>
                            <div>{p.evaluated_by_name || "—"}</div>
                            <div>{formatDateTime(p.evaluated_at)}</div>
                          </>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {writable && (
            <div className="flex items-center gap-3">
              <Button onClick={save} disabled={saving || !dirty} data-testid="eff-save">
                {saving ? "Saving…" : "Save evaluations"}
              </Button>
              {dirty && (
                <span className="text-xs text-muted-foreground" data-testid="eff-unsaved">
                  Unsaved changes
                </span>
              )}
              {data.evaluated > 0 && data.effective < data.evaluated && (
                <Badge variant="outline" className="ml-auto">
                  {data.evaluated - data.effective} need follow-up
                </Badge>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
